// src/pages/Checkins.js
//
// Money Conversations / Check-ins landing page.
//
// FIX (2026-05-07):
// - "Whose turn is it" now decided by actual response counts per user,
//   not by who initiated the conversation. Previously a partner who hadn't
//   answered would see "Waiting on Philip" because the code assumed the
//   initiator was always done.
// - Routing on "View progress" now respects current user's answer state.
//
import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { theme } from '../lib/theme'

const c = theme.colors
const f = theme.fonts

const formatDate = (dateString) => {
  if (!dateString) return ''
  const d = new Date(dateString)
  return d.toLocaleDateString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

const timeAgo = (dateString) => {
  if (!dateString) return ''
  const now = new Date()
  const then = new Date(dateString)
  const diffMs = now - then
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))
  if (diffDays === 0) return 'Today'
  if (diffDays === 1) return 'Yesterday'
  if (diffDays < 7) return `${diffDays} days ago`
  if (diffDays < 30) return `${Math.floor(diffDays / 7)}w ago`
  if (diffDays < 365) return `${Math.floor(diffDays / 30)}mo ago`
  return `${Math.floor(diffDays / 365)}y ago`
}

export default function Checkins() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [user, setUser] = useState(null)
  const [partnership, setPartnership] = useState(null)
  const [partnerProfile, setPartnerProfile] = useState(null)
  const [conversations, setConversations] = useState([])
  // Cached per-active-conversation answer state for both partners:
  // { activeConvoId, totalQuestions, currentUserAnswered, partnerAnswered }
  const [activeAnswerState, setActiveAnswerState] = useState(null)

  useEffect(() => { fetchAll() }, [])

  async function fetchAll() {
    setLoading(true)
    const { data: { user: u } } = await supabase.auth.getUser()
    if (!u) { setLoading(false); return }
    setUser(u)

    const { data: link } = await supabase.from('partner_links')
      .select('*')
      .or(`user_id_1.eq.${u.id},user_id_2.eq.${u.id}`)
      .eq('is_active', true)
      .maybeSingle()

    if (!link) {
      setLoading(false)
      return
    }
    setPartnership(link)

    const partnerId = link.user_id_1 === u.id ? link.user_id_2 : link.user_id_1
    const { data: pp } = await supabase.from('profiles')
      .select('full_name')
      .eq('id', partnerId)
      .maybeSingle()
    setPartnerProfile(pp)

    const { data: convos } = await supabase.from('money_conversations')
      .select('*')
      .eq('partnership_id', link.id)
      .order('initiated_at', { ascending: false })
    setConversations(convos || [])

    // For the active conversation (if any), find out who has actually answered.
    const activeConvo = (convos || []).find(c =>
      c.status === 'drafting' || c.status === 'awaiting_partner' || c.status === 'ready_to_review'
    )
    if (activeConvo) {
      // Fetch question count + responses by user
      const { data: qs } = await supabase
        .from('conversation_questions')
        .select('id')
        .eq('conversation_id', activeConvo.id)
      const total = (qs || []).length
      const cqIds = (qs || []).map(q => q.id)

      let currentUserAnswered = 0
      let partnerAnswered = 0
      if (cqIds.length > 0) {
        const { data: rs } = await supabase
          .from('conversation_responses')
          .select('user_id, answer_text, skipped')
          .in('conversation_question_id', cqIds)
        for (const r of (rs || [])) {
          const finished = !!r.answer_text || !!r.skipped
          if (!finished) continue
          if (r.user_id === u.id) currentUserAnswered++
          else if (r.user_id === partnerId) partnerAnswered++
        }
      }
      setActiveAnswerState({
        activeConvoId: activeConvo.id,
        totalQuestions: total,
        currentUserAnswered,
        partnerAnswered,
      })
    } else {
      setActiveAnswerState(null)
    }
    setLoading(false)
  }

  const startNewCheckin = async () => {
    if (!partnership) return
    const { data: convo, error } = await supabase
      .from('money_conversations')
      .insert({
        partnership_id: partnership.id,
        initiated_by_user_id: user.id,
        mode: 'async',
        status: 'drafting',
      })
      .select()
      .single()

    if (error) {
      console.error('Failed to start conversation:', error)
      return
    }
    navigate(`/checkins/${convo.id}/start`)
  }

  if (loading) {
    return <div style={styles.loading}>Loading…</div>
  }

  if (!partnership) {
    return (
      <div style={styles.page}>
        <header style={styles.header}>
          <h1 style={styles.title}>Check-ins</h1>
        </header>
        <div style={styles.empty}>
          <h2 style={styles.emptyTitle}>Invite your partner first</h2>
          <p style={styles.emptyText}>
            Money conversations are designed for couples. Connect with your
            partner from Settings, then come back here.
          </p>
        </div>
      </div>
    )
  }

  const partnerName = partnerProfile?.full_name?.split(' ')[0] || 'your partner'
  const activeConvo = conversations.find(co =>
    co.status === 'drafting' || co.status === 'awaiting_partner' || co.status === 'ready_to_review'
  )
  const completedConvos = conversations.filter(co => co.status === 'completed')
  const lastCompleted = completedConvos[0]

  return (
    <div style={styles.page}>
      <header style={styles.header}>
        <h1 style={styles.title}>Check-ins</h1>
        <p style={styles.tagline}>
          Money conversations with {partnerName}, made easy.
        </p>
      </header>

      {/* Active conversation card */}
      {activeConvo && (
        <ActiveConversationCard
          convo={activeConvo}
          answerState={activeAnswerState}
          partnerName={partnerName}
          onResume={(targetPath) => navigate(targetPath)}
        />
      )}

      {/* Stats row */}
      {completedConvos.length > 0 && (
        <section style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Conversations</div>
            <div style={styles.statValue}>{completedConvos.length}</div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Last check-in</div>
            <div style={styles.statValue}>
              {timeAgo(lastCompleted.completed_at)}
            </div>
          </div>
          <div style={styles.statCard}>
            <div style={styles.statLabel}>Streak</div>
            <div style={styles.statValue}>
              {calculateStreak(completedConvos)} {calculateStreak(completedConvos) === 1 ? 'month' : 'months'}
            </div>
          </div>
        </section>
      )}

      {/* Start button */}
      {!activeConvo && (
        <button
          type="button"
          onClick={startNewCheckin}
          style={styles.startBtn}
        >
          Start a new check-in
        </button>
      )}

      {/* History list */}
      {completedConvos.length > 0 ? (
        <section style={styles.historySection}>
          <h2 style={styles.sectionTitle}>Past conversations</h2>
          <div style={styles.historyList}>
            {completedConvos.map(convo => (
              <button
                key={convo.id}
                type="button"
                onClick={() => navigate(`/checkins/${convo.id}/review`)}
                style={styles.historyItem}
              >
                <div style={styles.historyMain}>
                  <div style={styles.historyDate}>
                    {formatDate(convo.completed_at)}
                  </div>
                  <div style={styles.historyMeta}>
                    {convo.mode === 'sync' ? '🤝 Sync' : '⏳ Async'}
                  </div>
                </div>
                <span style={{ color: c.inkMuted, fontSize: '18px', lineHeight: 1 }} aria-hidden="true">›</span>
              </button>
            ))}
          </div>
        </section>
      ) : !activeConvo && (
        <FirstTimeEmptyState onStart={startNewCheckin} partnerName={partnerName} />
      )}
    </div>
  )
}

// ──────────────────────────────────────────────────────────────
// Active conversation surfaced at the top.
// Decides label + CTA based on actual response counts, not initiator.
// ──────────────────────────────────────────────────────────────
function ActiveConversationCard({ convo, answerState, partnerName, onResume }) {
  let title, subtitle, ctaLabel, targetPath

  if (convo.status === 'drafting') {
    title = 'Pick up where you left off'
    subtitle = "You haven't finished setting up this conversation."
    ctaLabel = 'Resume'
    targetPath = `/checkins/${convo.id}/start`
  } else if (convo.status === 'ready_to_review') {
    title = 'Conversation ready ✨'
    subtitle = "You both answered. Time to see each other's responses together."
    ctaLabel = 'Reveal answers'
    targetPath = `/checkins/${convo.id}/review`
  } else {
    // awaiting_partner: decide based on actual answer state
    const total = answerState?.totalQuestions || 0
    const myCount = answerState?.currentUserAnswered || 0
    const theirCount = answerState?.partnerAnswered || 0
    const iAmDone = total > 0 && myCount >= total
    const theyAreDone = total > 0 && theirCount >= total

    if (!iAmDone) {
      // I haven't finished. Route me to /answer.
      if (myCount === 0) {
        title = 'Your turn'
        subtitle = `${partnerName} started a conversation. Add your answers when you're ready.`
      } else {
        title = 'Pick up where you left off'
        subtitle = `You've answered ${myCount} of ${total}. Keep going when you're ready.`
      }
      ctaLabel = myCount === 0 ? 'Answer questions' : 'Continue'
      targetPath = `/checkins/${convo.id}/answer`
    } else if (iAmDone && !theyAreDone) {
      title = `Waiting on ${partnerName}`
      subtitle = "You've answered. We'll let you know when they're done."
      ctaLabel = 'View your answers'
      targetPath = `/checkins/${convo.id}/answer`
    } else {
      // Both done but status hasn't flipped to ready_to_review yet (race condition).
      // Treat as ready to review.
      title = 'Conversation ready ✨'
      subtitle = "You both answered. Time to see each other's responses together."
      ctaLabel = 'Reveal answers'
      targetPath = `/checkins/${convo.id}/review`
    }
  }

  return (
    <div style={styles.activeCard}>
      <div style={styles.activeMain}>
        <div style={styles.activeTitle}>{title}</div>
        <div style={styles.activeSubtitle}>{subtitle}</div>
      </div>
      <button type="button" onClick={() => onResume(targetPath)} style={styles.activeCta}>
        {ctaLabel}
      </button>
    </div>
  )
}

// ──────────────────────────────────────────────────────────────
// Empty state for first-time users
// ──────────────────────────────────────────────────────────────
function FirstTimeEmptyState({ onStart, partnerName }) {
  return (
    <section style={styles.emptyHero}>
      <h2 style={styles.emptyHeroTitle}>Have your first money conversation</h2>
      <p style={styles.emptyHeroText}>
        Pick a few questions, answer them on your own time, then come together
        with {partnerName} to compare notes. No judgment, no scoring, just
        conversation.
      </p>
      <ul style={styles.emptyHeroList}>
        <li>Choose questions from a curated library or write your own</li>
        <li>Answer independently or in real-time together</li>
        <li>Review side-by-side and capture one thing to try next month</li>
      </ul>
      <button type="button" onClick={onStart} style={styles.startBtn}>
        Start your first check-in
      </button>
    </section>
  )
}

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
function calculateStreak(completedConvos) {
  if (completedConvos.length === 0) return 0
  const months = new Set()
  for (const c of completedConvos) {
    const d = new Date(c.completed_at)
    months.add(`${d.getFullYear()}-${d.getMonth()}`)
  }
  let streak = 0
  let cursor = new Date()
  while (months.has(`${cursor.getFullYear()}-${cursor.getMonth()}`)) {
    streak++
    cursor.setMonth(cursor.getMonth() - 1)
  }
  return streak
}

const styles = {
  page: { padding: '24px', maxWidth: '900px', margin: '0 auto' },
  loading: { padding: '40px', textAlign: 'center', color: c.inkMuted },
  header: { marginBottom: '24px' },
  title: {
    fontFamily: f.display, fontSize: '32px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.02em',
  },
  tagline: {
    color: c.inkMuted, fontSize: '14px', margin: '6px 0 0',
    lineHeight: 1.5, maxWidth: '600px',
  },
  empty: {
    textAlign: 'center', padding: '60px 24px',
    background: c.white, borderRadius: '16px', border: `1px solid ${c.line}`,
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  emptyTitle: {
    fontFamily: f.display, fontSize: '20px', fontWeight: 600,
    color: c.plum, margin: 0,
  },
  emptyText: {
    fontSize: '14px', color: c.inkMuted,
    maxWidth: '420px', lineHeight: 1.5, margin: 0,
  },
  emptyHero: {
    background: c.white, borderRadius: '16px', padding: '40px 28px',
    border: `1px solid ${c.line}`, textAlign: 'center',
    display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '12px',
  },
  emptyHeroTitle: {
    fontFamily: f.display, fontSize: '24px', fontWeight: 600,
    color: c.plum, margin: 0, letterSpacing: '-0.015em',
  },
  emptyHeroText: {
    fontSize: '15px', color: c.ink,
    maxWidth: '480px', lineHeight: 1.6, margin: 0,
  },
  emptyHeroList: {
    textAlign: 'left', color: c.ink, fontSize: '14px',
    lineHeight: 1.7, maxWidth: '420px', margin: '8px 0 12px',
    paddingLeft: '20px',
  },
  startBtn: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '10px',
    padding: '14px 24px', fontSize: '15px', fontWeight: 600,
    cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px',
    fontFamily: f.body || 'inherit',
    transition: 'background 100ms',
    marginTop: '8px',
  },
  activeCard: {
    background: c.greenSoft || '#e8f5e9',
    border: `1.5px solid ${c.green}`,
    borderRadius: '14px',
    padding: '18px 20px',
    marginBottom: '20px',
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
  },
  activeIcon: {
    width: '40px', height: '40px',
    borderRadius: '50%', background: c.white,
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: c.green, flexShrink: 0,
  },
  activeMain: { flex: 1, minWidth: 0 },
  activeTitle: {
    fontSize: '15px', fontWeight: 600, color: c.plum,
    fontFamily: f.display,
  },
  activeSubtitle: {
    fontSize: '13px', color: c.inkMuted, marginTop: '2px',
  },
  activeCta: {
    background: c.green, color: c.white,
    border: 'none', borderRadius: '8px',
    padding: '8px 14px', fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', whiteSpace: 'nowrap',
  },
  statsRow: {
    display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
    gap: '10px', marginBottom: '20px',
  },
  statCard: {
    background: c.cream2 || '#faf8f5',
    borderRadius: '10px', padding: '12px',
    border: `1px solid ${c.line}`, textAlign: 'center',
  },
  statLabel: {
    fontSize: '11px', color: c.inkMuted, fontWeight: 600,
    textTransform: 'uppercase', letterSpacing: '0.04em',
    marginBottom: '4px',
  },
  statValue: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, letterSpacing: '-0.015em',
  },
  historySection: { marginTop: '20px' },
  sectionTitle: {
    fontFamily: f.display, fontSize: '18px', fontWeight: 600,
    color: c.plum, margin: '0 0 12px', letterSpacing: '-0.015em',
  },
  historyList: {
    display: 'flex', flexDirection: 'column', gap: '8px',
  },
  historyItem: {
    background: c.white, border: `1px solid ${c.line}`,
    borderRadius: '10px', padding: '14px 16px',
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    cursor: 'pointer', textAlign: 'left',
    fontFamily: 'inherit', width: '100%',
  },
  historyMain: { display: 'flex', flexDirection: 'column', gap: '2px' },
  historyDate: { fontSize: '14px', fontWeight: 500, color: c.plum },
  historyMeta: { fontSize: '12px', color: c.inkMuted },
}
